import { useEffect, useMemo } from 'react'

import { useQuery } from '@apollo/client/react'

import { useCallbackRef } from '@buffer-mono/popcorn'

import { buildTagComparator } from '~publish/components/FilterByTag'
import { graphql } from '~publish/graphql'
import { useChannelAgnosticDraftsEnabled } from '~publish/hooks/useChannelAgnosticDraftsEnabled'
import { registerPostCountsFetcher } from '~publish/services/postCounts'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

const postEvents = [
  PusherEvent.POST_CREATED,
  PusherEvent.POST_UPDATED,
  PusherEvent.POST_DELETED,
  PusherEvent.POST_SENT,
  PusherEvent.POST_TAG_ADDED,
  PusherEvent.POST_TAG_REMOVED,
  PusherEvent.DRAFT_MOVED,
  PusherEvent.DRAFT_UPDATED,
  PusherEvent.DRAFT_APPROVED,
  PusherEvent.QUEUE_CHANGED,
]

export const GetUndatedDraftsCount = graphql(/* GraphQL */ `
  query GetUndatedDraftsCount(
    $organizationId: OrganizationId!
    $channelIds: [ChannelId!]
    $tags: TagComparator
  ) {
    posts(
      input: {
        organizationId: $organizationId
        filter: {
          channelIds: $channelIds
          tags: $tags
          status: [draft, needs_approval]
          dueAtPresence: absent
        }
      }
      first: 1
    ) {
      totalCount
    }
  }
`)

// Channel-less drafts have no channel/tag, so this counts them for the whole
// organization; the caller only includes it in the all-channels view.
export const GetUndatedContentItemDraftsCount = graphql(/* GraphQL */ `
  query GetUndatedContentItemDraftsCount($organizationId: OrganizationId!) {
    contentItems(
      input: {
        organizationId: $organizationId
        filter: {
          contentStatus: draftContent
          targetDate: { presence: absent }
        }
      }
      first: 1
    ) {
      totalCount
    }
  }
`)

type UseUndatedDraftsCountOptions = {
  organizationId: string | undefined | null
  channelIds?: string[]
  tagIds?: string[]
}

function getEventChannelId(data: unknown): string | undefined {
  if (
    data &&
    typeof data === 'object' &&
    'profile_id' in data &&
    typeof data.profile_id === 'string'
  ) {
    return data.profile_id
  }
  return undefined
}

export function useUndatedDraftsCount({
  organizationId,
  channelIds,
  tagIds,
}: UseUndatedDraftsCountOptions): number | undefined {
  const variables = useMemo(
    () => ({
      organizationId: organizationId ?? '',
      channelIds: channelIds?.length ? channelIds : undefined,
      tags: buildTagComparator(tagIds),
    }),
    [organizationId, channelIds, tagIds],
  )
  const { data, refetch } = useQuery(GetUndatedDraftsCount, {
    variables,
    skip: !organizationId,
    fetchPolicy: 'no-cache',
  })

  // Channel-less drafts belong to no channel, so only count them in the
  // all-channels view — matching what the No Date sidebar surfaces.
  const channelAgnosticDraftsEnabled = useChannelAgnosticDraftsEnabled()
  const includeContentItemDrafts =
    channelAgnosticDraftsEnabled && !channelIds?.length
  const { data: contentItemData, refetch: refetchContentItems } = useQuery(
    GetUndatedContentItemDraftsCount,
    {
      variables: { organizationId: organizationId ?? '' },
      skip: !organizationId || !includeContentItemDrafts,
      fetchPolicy: 'no-cache',
    },
  )

  const refetchCount = useCallbackRef(async (): Promise<void> => {
    if (!organizationId) return
    await Promise.all([
      refetch(),
      includeContentItemDrafts ? refetchContentItems() : Promise.resolve(),
    ])
  })

  useEffect(() => registerPostCountsFetcher(refetchCount), [refetchCount])

  usePusherEvent(postEvents, (event: unknown): void => {
    const eventChannelId = getEventChannelId(event)
    if (
      channelIds?.length &&
      eventChannelId &&
      !channelIds.includes(eventChannelId)
    ) {
      return
    }
    void refetchCount()
  })

  const postsCount = data?.posts.totalCount
  if (postsCount === undefined) return undefined
  const contentItemsCount = includeContentItemDrafts
    ? (contentItemData?.contentItems.totalCount ?? 0)
    : 0
  return postsCount + contentItemsCount
}
